import { useEffect, useRef } from "react";

/**
 * Automatically log the user out after a period of inactivity.
 *
 * Inactivity = no mouse move, click, key press, scroll or touch for `timeoutMs`.
 * A 60s warning toast is shown before the final logout so the user can react.
 *
 * Usage:
 *   useIdleLogout(isLoggedIn, () => handleLogout(), 30 * 60 * 1000)
 */
export default function useIdleLogout(isActive, onLogout, timeoutMs = 30 * 60 * 1000, warnMs = 60 * 1000) {
  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const warnedRef = useRef(false);

  useEffect(() => {
    if (!isActive) return;

    const clearTimers = () => {
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      warnedRef.current = false;
    };

    const arm = () => {
      clearTimers();
      warnTimerRef.current = setTimeout(() => {
        warnedRef.current = true;
        // Lazy import to avoid circular issues with the toaster
        import("sonner").then(({ toast }) => {
          toast.warning(
            "Vous allez être déconnecté pour inactivité dans 1 minute",
            { duration: 10000 }
          );
        }).catch(() => {});
      }, Math.max(0, timeoutMs - warnMs));
      logoutTimerRef.current = setTimeout(() => {
        onLogout();
      }, timeoutMs);
    };

    const reset = () => arm();

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "visibilitychange"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    arm();

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      clearTimers();
    };
  }, [isActive, onLogout, timeoutMs, warnMs]);
}
